import type {
  TransformedItemPost,
  TransformedItemContributor,
} from '@highereducation/wordpress';
import type {
  ArticleCardProps,
  TimelineMarkerColor,
} from '@highereducation/hen-ui';

/** Keep in sync with eventing `PASSIVE_METADATA_SUFFIXES` (`location`, `name`). */
const HEN_METADATA_ATTRS = ['data-hen-location', 'data-hen-name'] as const;

export type HenMetadataAttrs = Partial<
  Record<(typeof HEN_METADATA_ATTRS)[number], string>
>;

/** Picks only the eventing metadata attributes that shortcodes may forward. */
export function pickHenMetadataAttrs(
  attrs: Record<string, unknown>,
): HenMetadataAttrs {
  return Object.fromEntries(
    HEN_METADATA_ATTRS.flatMap((attr) => {
      const value = attrs[attr];
      return typeof value === 'string' && value ? [[attr, value]] : [];
    }),
  ) as HenMetadataAttrs;
}

/**
 * Returns true when a shortcode attribute string carries a truthy value.
 * Accepts "true" (case-insensitive, whitespace-trimmed).
 * Anything else — including an omitted attribute — resolves to false.
 */
export const parseTruthy = (val?: string): boolean =>
  (val ?? '').toLowerCase().trim() === 'true';

/** Feature toggles from shortcode attrs (`show-image`). Omitted / empty → show. */
export const parseShowImage = (val: unknown): boolean => {
  if (typeof val === 'boolean') return val;
  const s = String(val ?? '')
    .toLowerCase()
    .trim();
  return s === '' || !['false', '0', 'no'].includes(s);
};

/**
 * ArticleRow shortcode `card-variant` → {@link ArticleCardProps.variant}.
 * `excerpt` (or legacy `alternative`) → excerpt rail; `byline` or omitted → byline.
 */
export function parseArticleRowVariant(val?: string) {
  switch ((val ?? '').toLowerCase().trim()) {
    case 'excerpt':
    case 'alternative':
      return 'excerpt' as const;
    default:
      return 'byline' as const;
  }
}

const UNICODE_MAX_SCALAR = 0x10ffff;
const SURROGATE_MIN = 0xd800;
const SURROGATE_MAX = 0xdfff;

/**
 * Decode `&#...;` / `&#x...;` only when the value is a valid Unicode scalar (not surrogate).
 * Invalid values leave the original entity substring so shortcode rendering cannot throw.
 */
function decodeNumericEntity(
  entity: string,
  digits: string,
  radix: 10 | 16,
): string {
  const codePoint = Number.parseInt(digits, radix);
  if (
    !Number.isFinite(codePoint) ||
    !Number.isInteger(codePoint) ||
    codePoint < 0 ||
    codePoint > UNICODE_MAX_SCALAR ||
    (codePoint >= SURROGATE_MIN && codePoint <= SURROGATE_MAX)
  ) {
    return entity;
  }
  return String.fromCodePoint(codePoint);
}

/** WP excerpt HTML → one line of plain text for ArticleCard (tags + entities). */
function excerptPlainFromWp(html: string): string {
  if (!html) return '';
  let s = html.replace(/<[^>]+>/g, ' ');
  s = s.replace(/&#(\d+);/g, (entity, dec) =>
    decodeNumericEntity(entity, dec, 10),
  );
  s = s.replace(/&#x([0-9a-f]+);/gi, (entity, hex) =>
    decodeNumericEntity(entity, hex, 16),
  );
  return s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

export type ToArticleCardPropsOptions = {
  variant?: ArticleCardProps['variant'];
  layout?: ArticleCardProps['layout'];
};

/**
 * Maps a CMS post to ArticleCard props.
 * Use `{ variant: 'excerpt' }` for row rails that show excerpt from `post.content`.
 * Use `{ layout: 'overlay' }` for full-bleed image tiles (e.g. Trends rails).
 */
export function toArticleCardProps(
  post: TransformedItemPost,
  options?: ToArticleCardPropsOptions,
): ArticleCardProps {
  const cmsAuthor = post.contributors?.[0] as
    | TransformedItemContributor
    | undefined;
  const base: ArticleCardProps = {
    image: post.image || '',
    imageAlt: post.imagealt || post.title,
    title: post.title,
    href: post.link,
    ...(options?.layout ? { layout: options.layout } : {}),
  };
  if (options?.variant === 'excerpt') {
    return {
      ...base,
      variant: 'excerpt',
      excerpt: excerptPlainFromWp(post.content),
      brow: post.brow,
    };
  }
  return {
    ...base,
    date: post.date,
    author: cmsAuthor?.name,
    brow: post.brow,
  };
}

/**
 * Server-safe mirror of hen-ui's valid Timeline marker colors. hen-ui's own
 * `isTimelineMarkerColor` is exported from a `'use client'` barrel, so calling
 * it from these server-rendered shortcodes throws an RSC "called a client
 * function from the server" error. Keyed by `TimelineMarkerColor` so typecheck
 * fails if hen-ui adds or removes a color — the set cannot silently drift.
 */
const TIMELINE_MARKER_COLOR_SET: Record<TimelineMarkerColor, true> = {
  default: true,
  primary: true,
  secondary: true,
  accent: true,
  neutral: true,
  success: true,
  warning: true,
  error: true,
};

/** Server-safe type guard for a Timeline marker color shortcode attribute. */
export const isTimelineMarkerColor = (
  value: unknown,
): value is TimelineMarkerColor =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(TIMELINE_MARKER_COLOR_SET, value);
